// Practice policy — the tunable numbers, for Firm Settings.
//
// These were module constants until a question about one of them turned
// out to be firm policy rather than a technical choice. The screen has
// to carry that: each row says what the number actually changes, in the
// language of case work rather than of the code it came from.
//
// Values that still sit at the default are labelled as such. That is the
// useful distinction on this screen — not "is this valid" but "has
// anyone here decided this, or is it just what we shipped".

import React, { useCallback, useEffect, useState } from "react";
import { C } from "../constants.js";
import { apiFetch } from "../api.js";

const SECTION_TITLES = {
  pipeline: "Pipeline and staleness",
  medical: "Medical records",
  deadlines: "Deadlines and demands",
  calendar: "Calendar",
  intake: "Client intake",
};

const SECTION_BLURBS = {
  pipeline: "When the dashboard starts calling a matter idle, aging or "
          + "stale. Activity in Clio counts toward this, not only work "
          + "done in this app.",
  medical: "Thresholds for chasing records and for flagging breaks in "
         + "treatment.",
  deadlines: "How far ahead statutory dates are warned about, and when a "
           + "demand deadline is short enough to question.",
  calendar: "Where deadline reminders are written. Reminders update in "
          + "place rather than duplicating, and are removed when a "
          + "deadline is closed.",
  intake: "What happens to a client's answers when they submit the intake "
        + "form. Everything they type is saved and shown on the matter "
        + "either way — this decides what is written onto the file "
        + "without anyone checking it first.",
};

export function PracticePolicySection({ canEdit, showNotification }) {
  const [rows, setRows] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(null);

  const load = useCallback(async () => {
    try {
      const d = await apiFetch("/api/current-firm/policy");
      setRows(d.settings || []);
      setDrafts({});
    } catch (e) {
      showNotification?.(`Practice settings failed to load: ${e.message}`, "error");
      setRows([]);
    }
  }, [showNotification]);

  useEffect(() => { load(); }, [load]);

  const save = async (row) => {
    const raw = drafts[row.key];
    if (raw === undefined || String(raw).trim() === "") return;
    setSaving(row.key);
    try {
      await apiFetch("/api/current-firm/policy", {
        method: "PATCH",
        body: JSON.stringify({ key: row.key, value: Number(raw) }),
      });
      showNotification?.(`${row.label} saved.`, "success");
      await load();
    } catch (e) {
      // The server explains the allowed range; pass it through rather
      // than replacing it with something vaguer.
      showNotification?.(e.message, "error");
    }
    setSaving(null);
  };

  const reset = async (row) => {
    setSaving(row.key);
    try {
      await apiFetch("/api/current-firm/policy", {
        method: "PATCH",
        body: JSON.stringify({ key: row.key, value: row.default }),
      });
      await load();
    } catch (e) {
      showNotification?.(e.message, "error");
    }
    setSaving(null);
  };

  if (rows === null) return null;

  const sections = [...new Set(rows.map((r) => r.section))];

  return React.createElement("div", { style: cardStyle },
    React.createElement("h2", { style: h2Style }, "Practice settings"),
    React.createElement("p", {
      style: { fontSize: "13px", color: C.textMuted, margin: "6px 0 4px",
               lineHeight: 1.55, maxWidth: "62ch" },
    }, "How this firm wants the app to judge a case. Every one has a "
     + "sensible default, so nothing here needs setting before the app "
     + "works — change them when your practice disagrees."),

    ...sections.map((sec) => React.createElement("div", {
      key: sec,
      style: { marginTop: "24px" },
    },
      React.createElement("h3", {
        style: { fontSize: "13px", color: C.navy, fontWeight: 700, margin: "0 0 3px" },
      }, SECTION_TITLES[sec] || sec),
      React.createElement("p", {
        style: { fontSize: "12px", color: C.textMuted, margin: "0 0 12px",
                 maxWidth: "62ch", lineHeight: 1.5 },
      }, SECTION_BLURBS[sec] || ""),

      ...rows.filter((r) => r.section === sec).map((row) => {
        const draft = drafts[row.key];
        const dirty = draft !== undefined && Number(draft) !== row.value;
        return React.createElement("div", {
          key: row.key,
          className: "stack-row",
          style: {
            display: "flex", gap: "12px", alignItems: "baseline",
            padding: "12px 0", borderTop: `1px solid ${C.warmGray}`,
          },
        },
          React.createElement("div", { style: { flex: 1, minWidth: 0 } },
            React.createElement("div", {
              style: { display: "flex", gap: "8px", alignItems: "baseline",
                       flexWrap: "wrap" },
            },
              React.createElement("span", {
                style: { fontSize: "14px", fontWeight: 600, color: C.navy },
              }, row.label),
              row.is_default && React.createElement("span", {
                style: {
                  fontSize: "10px", fontWeight: 700, textTransform: "uppercase",
                  letterSpacing: "0.06em", color: C.textMuted,
                  border: `1px solid ${C.warmGray}`, borderRadius: "4px",
                  padding: "1px 6px",
                },
              }, "Default"),
            ),
            React.createElement("div", {
              style: { fontSize: "12px", color: C.textMuted, marginTop: "3px",
                       lineHeight: 1.5 },
            }, row.help),
          ),

          React.createElement("div", {
            style: { display: "flex", gap: "7px", alignItems: "center",
                     flexShrink: 0 },
          },
            // A choice renders as a select; everything else is a number.
            // Options that are not connected yet are shown but disabled,
            // so the roadmap is visible without being selectable — the
            // server refuses them too, with the reason.
            row.is_choice
              ? React.createElement("select", {
                  value: draft !== undefined ? draft : row.value,
                  disabled: !canEdit,
                  onChange: (e) => setDrafts((p) => ({ ...p, [row.key]: e.target.value })),
                  style: {
                    padding: "6px 9px", borderRadius: "6px",
                    border: `1px solid ${dirty ? C.navy : C.warmGray}`,
                    fontSize: "13px", background: "#fff", minWidth: "170px",
                  },
                },
                  ...(row.choices || []).map((c) =>
                    React.createElement("option", {
                      key: c.value, value: c.value, disabled: !c.available,
                    }, c.available ? c.label : `${c.label} — not connected`)),
                )
              : React.createElement("input", {
                  type: "number",
                  value: draft !== undefined ? draft : row.value,
                  min: row.minimum, max: row.maximum,
                  disabled: !canEdit,
                  onChange: (e) => setDrafts((p) => ({ ...p, [row.key]: e.target.value })),
                  onKeyDown: (e) => { if (e.key === "Enter" && dirty) save(row); },
                  style: {
                    width: "78px", padding: "6px 9px", borderRadius: "6px",
                    border: `1px solid ${dirty ? C.navy : C.warmGray}`,
                    fontSize: "13px", textAlign: "right",
                    fontVariantNumeric: "tabular-nums", background: "#fff",
                  },
                }),
            React.createElement("span", {
              style: { fontSize: "11px", color: C.textMuted, minWidth: "62px" },
            }, row.unit),
            canEdit && dirty && React.createElement("button", {
              onClick: () => save(row), disabled: saving === row.key,
              style: primaryBtn,
            }, saving === row.key ? "Saving…" : "Save"),
            canEdit && !dirty && !row.is_default && React.createElement("button", {
              onClick: () => reset(row), disabled: saving === row.key,
              style: quietBtn, title: `Back to ${row.default} ${row.unit}`,
            }, "Reset"),
          ),
        );
      }),
    )),
  );
}

const cardStyle = {
  background: "#fff", borderRadius: "12px", padding: "20px 24px",
  border: "1px solid #E8E3DC", marginBottom: "20px",
};
const h2Style = {
  fontSize: "13px", color: "#6B6660", textTransform: "uppercase",
  letterSpacing: "0.08em", fontWeight: 600, margin: 0,
};
const quietBtn = {
  border: "1px solid #E8E3DC", background: "#fff", borderRadius: "6px",
  padding: "6px 11px", fontSize: "11px", fontWeight: 600,
  color: "#1B2A4A", cursor: "pointer", flexShrink: 0,
};
const primaryBtn = {
  border: "1px solid #1B2A4A", background: "#1B2A4A", borderRadius: "6px",
  padding: "6px 13px", fontSize: "11px", fontWeight: 600,
  color: "#fff", cursor: "pointer", flexShrink: 0,
};
